import { FC, MouseEvent, ReactNode } from 'react';
import { Box, Button, SxProps, Theme, useTheme } from '@mui/material';

interface Props {
  icon: ReactNode;
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'error' | 'primary' | 'secondary' | 'success' | 'info' | 'warning';
  size?: number;
  ariaLabel?: string;
  expandDirection?: 'left' | 'right';
  sx?: SxProps<Theme>;
}

const DEFAULT_SIZE = 40;
const TRANSITION_MS = 280;

export const QcsHoverExpandButton: FC<Props> = ({
  icon,
  children,
  onClick,
  loading,
  disabled,
  variant = 'text',
  color,
  size = DEFAULT_SIZE,
  ariaLabel,
  expandDirection = 'right',
  sx,
}) => {
  const theme = useTheme();
  const expandsLeft = expandDirection === 'left';
  const iconSlotClass = expandsLeft ? '& .MuiButton-endIcon' : '& .MuiButton-startIcon';
  const iconFontSize = Math.round(size * 0.6);

  const expandedSx = {
    width: 'auto',
    ...(expandsLeft ? { pl: '18px' } : { pr: '18px' }),
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    boxShadow: theme.shadows[2],
    [iconSlotClass]: expandsLeft
      ? { ml: '8px', color: 'inherit' }
      : { mr: '8px', color: 'inherit' },
    '& .qcs-hover-expand-label': { opacity: 1, maxWidth: 300 },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        display: 'inline-flex',
        verticalAlign: 'middle',
      }}
    >
      <Button
        variant={variant}
        color={color}
        loading={loading}
        disabled={disabled}
        onClick={onClick}
        startIcon={expandsLeft ? undefined : icon}
        endIcon={expandsLeft ? icon : undefined}
        aria-label={ariaLabel}
        sx={{
          position: 'absolute',
          top: 0,
          ...(expandsLeft ? { right: 0 } : { left: 0 }),
          minWidth: size,
          width: size,
          height: size,
          borderRadius: `${size / 2}px`,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: '8px',
          paddingRight: '8px',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          justifyContent: expandsLeft ? 'flex-end' : 'flex-start',
          zIndex: 1,
          transition: theme.transitions.create(
            ['width', 'padding', 'background-color', 'box-shadow', 'color'],
            { duration: TRANSITION_MS, easing: theme.transitions.easing.easeInOut },
          ),
          [iconSlotClass]: {
            ml: 0,
            mr: 0,
            '& > *:nth-of-type(1)': { fontSize: iconFontSize },
            transition: theme.transitions.create(['margin'], {
              duration: TRANSITION_MS,
              easing: theme.transitions.easing.easeInOut,
            }),
          },
          '& .qcs-hover-expand-label': {
            opacity: 0,
            maxWidth: 0,
            display: 'inline-block',
            overflow: 'hidden',
            transition: theme.transitions.create(['opacity', 'max-width'], {
              duration: TRANSITION_MS,
              easing: theme.transitions.easing.easeInOut,
            }),
          },
          '&:hover, &:focus-visible': expandedSx,
          ...sx,
        }}
      >
        <Box component="span" className="qcs-hover-expand-label">
          {children}
        </Box>
      </Button>
    </Box>
  );
};
